(() => {
  if (window.MagmaEnquiry) { window.MagmaEnquiry.init(document); return; }
  const instances = new Map();
  // A native success response and its same-tab validated attempt must agree.
  // Store only random IDs and form/page identity; never store contact values.
  function createEnquirySuccessPublisher(root, form, returnTo, originalReturnTo) {
    const parameter = 'magma_enquiry_id';
    const prefix = 'magma.enquiry.receipt.v1:';
    const maximumAge = 2 * 60 * 60 * 1000;
    const validId = value => typeof value === 'string' && /^[a-zA-Z0-9-]{16,80}$/.test(value);
    const eligible = () => location.protocol === 'https:' && root.dataset.enquiryHost === location.hostname && Boolean(root.dataset.enquiryHost) && root.dataset.editor !== 'true' && root.dataset.previewSuccess !== 'true' && !window.Shopify?.designMode;
    function returnTarget(value) {
      const target = new URL(value, location.origin);
      if (target.origin !== location.origin || target.hash !== `#${form.id}`) return null;
      return target;
    }
    function acceptedId() {
      const current = new URL(location.href);
      if (current.searchParams.has(parameter)) return current.searchParams.get(parameter);
      // Shopify's native error/preview return can recover the requested target
      // from form_key. Reading this target never proves acceptance by itself.
      const key = current.searchParams.get('form_key');
      if (!key || current.searchParams.get('form_type') !== 'contact') return null;
      const snapshot = JSON.parse(sessionStorage.getItem(key));
      if (snapshot?.__shopify_v !== 1 || snapshot.data?.form_type !== 'contact' || typeof snapshot.data.return_to !== 'string') return null;
      const target = returnTarget(snapshot.data.return_to);
      if (!target || target.pathname !== current.pathname || target.searchParams.get('view') !== current.searchParams.get('view')) return null;
      return target.searchParams.get(parameter);
    }
    function prepare() {
      if (!eligible() || !returnTo || typeof originalReturnTo !== 'string') return;
      try {
        const target = returnTarget(originalReturnTo);
        if (!target || !/^\/[a-zA-Z0-9/_-]*$/.test(location.pathname) || !/^[a-zA-Z0-9_-]+$/.test(form.id)) return;
        let id;
        if (typeof crypto.randomUUID === 'function') id = crypto.randomUUID();
        else if (typeof crypto.getRandomValues === 'function') id = [...crypto.getRandomValues(new Uint8Array(16))].map(value => value.toString(16).padStart(2, '0')).join('');
        if (!validId(id)) return;
        const receipt = {id, formId:form.id, pagePath:location.pathname, returnPath:target.pathname, returnView:target.searchParams.get('view'), createdAt:Date.now(), status:'pending'};
        sessionStorage.setItem(prefix + id, JSON.stringify(receipt));
        target.searchParams.set(parameter, id);
        returnTo.value = target.pathname + target.search + target.hash;
      } catch {}
    }
    function publish() {
      if (!eligible()) return;
      const accepted = form.querySelector('[data-enquiry-server-accepted]');
      if (!accepted || accepted.dataset.enquiryFormId !== form.id || accepted.dataset.enquiryHost !== location.hostname) return;
      try {
        const id = acceptedId();
        if (!validId(id)) return;
        const key = prefix + id, receipt = JSON.parse(sessionStorage.getItem(key));
        if (!receipt || receipt.status !== 'pending' || receipt.id !== id || receipt.formId !== form.id || receipt.returnPath !== location.pathname || receipt.returnView !== new URL(location.href).searchParams.get('view') || location.hash !== `#${form.id}`) return;
        const age = Date.now() - receipt.createdAt;
        if (!Number.isFinite(age) || age < 0 || age > maximumAge || !/^\/[a-zA-Z0-9/_-]*$/.test(receipt.pagePath)) return;
        const layer = window.dataLayer || (window.dataLayer = []);
        if (typeof layer.push !== 'function') return;
        // Commit before publishing: duplicate scripts, refreshes and bfcache
        // restores must not turn one accepted enquiry into another lead.
        sessionStorage.setItem(key, JSON.stringify({...receipt, status:'published'}));
        layer.push({event:'magma_enquiry_success', form_id:receipt.formId, page_path:receipt.pagePath, event_id:id});
      } catch {}
    }
    return {prepare, publish};
  }

  const mobile = matchMedia('(max-width: 767px)');
  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
  const updateBody = () => document.body.classList.toggle('me-has-sticky', [...instances.values()].some(instance => instance.sticky && !instance.sticky.hidden));

  function mount(root) {
    if (instances.has(root)) return;
    const form = root.querySelector('form');
    const body = root.querySelector('[data-enquiry-body]');
    const receipt = root.querySelector('[data-enquiry-success]');
    const sticky = root.querySelector('[data-enquiry-sticky]');
    const abort = new AbortController();
    const listen = (target, event, callback, options = {}) => target.addEventListener(event, callback, {...options, signal: abort.signal});
    const stages = [...body.querySelectorAll('[data-enquiry-stage]')];
    const fallback = body.querySelector('[data-enquiry-fallback]');
    const fallbackSlot = body.querySelector('[data-enquiry-fallback-slot]');
    const submitAction = body.querySelector('[data-enquiry-submit]');
    if (fallback && stages.length) {
      stages.at(-1).querySelector('.me-fields').append(fallback);
      fallbackSlot.remove();
    }
    if (submitAction && stages.length) {
      const lastStage = stages.at(-1);
      lastStage.insertBefore(submitAction, lastStage.querySelector('[data-enquiry-note-after]'));
    }
    body.querySelectorAll('[data-enquiry-process]').forEach(process => {
      const count = process.querySelectorAll('ol > li').length;
      if (!count) return;
      const columns = Number(process.style.getPropertyValue('--me-process-columns')) || 3;
      const mobileColumns = Number(process.style.getPropertyValue('--me-mobile-process-columns')) || 3;
      process.style.setProperty('--me-process-columns', Math.min(count, columns));
      process.style.setProperty('--me-mobile-process-columns', Math.min(count, mobileColumns));
    });
    const processGroup = body.querySelector('[data-enquiry-process]');
    const formContent = body.querySelector('[data-enquiry-form-content]');
    const processColumns = processGroup ? Number(processGroup.style.getPropertyValue('--me-process-columns')) || 3 : 0;
    const processMobileColumns = processGroup ? Number(processGroup.style.getPropertyValue('--me-mobile-process-columns')) || 3 : 0;
    function placeProcess() {
      if (!processGroup || !formContent) return;
      const bodyWidth = body.getBoundingClientRect().width;
      const processStyles = getComputedStyle(processGroup);
      const panePercentage = parseFloat(processStyles.getPropertyValue('--me-process-pane-width')) || 35;
      const processPadding = (parseFloat(processStyles.paddingLeft) || 0) + (parseFloat(processStyles.paddingRight) || 0);
      const firstMarker = processGroup.querySelector('.me-process-number');
      const requestedMarkerDiameter = firstMarker ? parseFloat(getComputedStyle(firstMarker).minHeight) || 0 : 0;
      const sidePane = ['left', 'right'].includes(processGroup.dataset.processPlacement);
      const narrowPane = sidePane && bodyWidth > 0 && bodyWidth * panePercentage / 100 < processPadding + Math.max(160, requestedMarkerDiameter);
      const narrow = mobile.matches || (bodyWidth > 0 && bodyWidth < 640) || narrowPane;
      root.classList.toggle('me-narrow-layout', narrow);
      const placement = narrow ? processGroup.dataset.processMobilePlacement : processGroup.dataset.processPlacement;
      if (placement === 'below' || placement === 'right') {
        if (body.lastElementChild !== processGroup) body.append(processGroup);
      } else if (processGroup.nextElementSibling !== formContent) body.insertBefore(processGroup, formContent);
      const grid = processGroup.querySelector('ol');
      const items = [...grid.children];
      const width = grid.getBoundingClientRect().width;
      if (!width) return;
      const gap = parseFloat(getComputedStyle(grid).columnGap) || (narrow ? 12 : 24);
      const maximum = narrow ? processMobileColumns : processColumns;
      const capacity = Math.max(1, Math.floor((width + gap + 0.5) / ((narrow ? 72 : 160) + gap)));
      const columns = Math.min(items.length, maximum, capacity);
      processGroup.style.setProperty('--me-process-effective-columns', String(columns));
      processGroup.setAttribute('data-process-layout-ready', '');
      const cellWidth = (width - (columns - 1) * gap) / columns;
      items.forEach((item, index) => {
        const marker = item.querySelector('.me-process-number');
        const markerWidth = marker ? marker.getBoundingClientRect().width : 0;
        item.classList.toggle('me-process-step--compact', (processGroup.classList.contains('me-process--beside') || processGroup.classList.contains('me-process--inline')) && cellWidth < markerWidth + 12 + 120);
        item.toggleAttribute('data-process-row-end', index % columns === columns - 1 || index === items.length - 1);
        item.style.setProperty('--me-connector-start', index % columns === 0 ? '50%' : '-12px');
        item.style.setProperty('--me-connector-end', index % columns === columns - 1 || index === items.length - 1 ? '50%' : '-12px');
      });
    }
    placeProcess();
    if (typeof mobile.addEventListener === 'function') listen(mobile, 'change', placeProcess);
    const processObserver = processGroup && typeof ResizeObserver === 'function' ? new ResizeObserver(placeProcess) : null;
    processObserver?.observe(body);
    if (processGroup) processObserver?.observe(processGroup.querySelector('ol'));
    listen(window, 'resize', placeProcess);
    const progress = body.querySelector('[data-enquiry-progress]');
    if (progress) {
      const list = progress.querySelector('ol');
      list.replaceChildren();
      list.style.setProperty('--me-stage-columns', Math.min(stages.length, 3));
      list.style.setProperty('--me-mobile-stage-columns', Math.min(stages.length, 2));
      stages.forEach((stage, index) => {
        const item = document.createElement('li');
        item.setAttribute('data-enquiry-progress-item', '');
        const button = document.createElement('button');
        button.type = 'button';
        button.disabled = true;
        button.dataset.enquiryStageTarget = String(index);
        const number = document.createElement('span');
        number.className = 'me-stage-number';
        number.textContent = String(index + 1);
        const label = document.createElement('span');
        label.className = 'me-stage-label';
        const stageLabel = (stage.dataset.stageLabel || '').trim() || stage.querySelector('h3')?.textContent.trim() || `${root.dataset.stageFallbackLabel || ''} ${index + 1}`.trim();
        stage.dataset.stageLabel = stageLabel;
        label.textContent = stageLabel;
        button.append(number, label);
        item.append(button);
        list.append(item);
      });
    }
    const progressItems = [...body.querySelectorAll('[data-enquiry-progress-item]')];
    const intro = body.querySelector('[data-enquiry-intro]');
    const errors = body.querySelector('[data-enquiry-errors]');
    const fields = [...body.querySelectorAll('[data-enquiry-field]')];
    const submitButton = form.querySelector('[type="submit"]');
    const submitLabel = submitButton?.textContent;
    const submitInitiallyDisabled = submitButton?.disabled;
    const initialBusy = form.getAttribute('aria-busy');
    const requiredProbe = document.createElement('input');
    requiredProbe.required = true;
    const whitespaceError = root.dataset.whitespaceError || requiredProbe.validationMessage;
    const whitespaceInvalid = new Set();
    let current = 0, complete = false, frame = 0, submitTimer = 0, submitFeedbackTimer = 0, pendingSubmitEvent = null, pendingCaptcha = false, submitting = false, navigationTimer = 0, navigating = false, observer;
    let hash = location.hash.slice(1);
    try { hash = decodeURIComponent(hash); } catch {}
    const roots = [...document.querySelectorAll('[data-magma-enquiry]')];
    const returnTo = form.querySelector('input[name="return_to"]');
    const originalReturnTo = root.dataset.enquiryReturnTo ?? returnTo?.getAttribute('value');
    const originalSource = root.dataset.enquirySource ?? form.querySelector('[name="contact[enquiry_source]"]')?.value ?? '';
    const leadSuccess = createEnquirySuccessPublisher(root, form, returnTo, originalReturnTo);
    const contactControls = [...form.querySelectorAll('input[name^="contact["],select[name^="contact["],textarea[name^="contact["]')];
    function restoreSubmittedState() {
      const params = new URLSearchParams(location.search);
      const key = params.get('form_key');
      if (!key || params.get('form_type') !== 'contact' || receipt) return;
      try {
        const saved = JSON.parse(sessionStorage.getItem(key));
        if (saved?.__shopify_v !== 1 || saved.data?.form_type !== 'contact') return;
        const target = new URL(saved.data.return_to, location.origin);
        if (target.origin !== location.origin || target.pathname !== location.pathname) return;
        if (returnTo && originalReturnTo != null) returnTo.value = originalReturnTo;
        const submitted = target.hash === `#${form.id}`;
        contactControls.forEach(input => {
          if (input.name === 'contact[enquiry_source]') { input.value = originalSource; return; }
          const savedValue = submitted && typeof saved.data[input.name] === 'string' ? saved.data[input.name] : input.dataset.enquiryDefaultValue ?? '';
          if (input.matches('input[type="checkbox"],input[type="radio"]')) {
            input.value = input.dataset.enquiryOptionValue ?? input.getAttribute('value');
            input.checked = submitted && savedValue === input.value;
          } else {
            if (input.options) [...input.options].forEach(option => { if (option.dataset.enquiryOptionValue !== undefined) option.value = option.dataset.enquiryOptionValue; });
            input.value = savedValue;
          }
        });
        if (!submitted) form.querySelectorAll('[name="form_key"]').forEach(input => input.remove());
      } catch {}
    }
    restoreSubmittedState();
    const conditionalFields = fields.filter(field => field.dataset.fieldRole === 'custom' && field.dataset.enquiryConditionKey).map(field => ({
      field,
      key: field.dataset.enquiryConditionKey,
      values: (field.dataset.enquiryConditionValues || '').split(/\r?\n/).map(value => value.trim()).filter(Boolean),
      required: field.dataset.fieldRequired === 'true',
      controls: [...field.querySelectorAll('input,select,textarea')].map(input => ({input, disabled: input.disabled}))
    }));
    function syncConditionalFields() {
      for (const condition of conditionalFields) {
        const source = fields.find(field => field.dataset.fieldKey === condition.key);
        const selected = source ? [...source.querySelectorAll('input,select,textarea')].filter(input => !input.disabled && (!input.matches('[type="checkbox"],[type="radio"]') || input.checked)).flatMap(input => input.matches('select[multiple]') ? [...input.selectedOptions].map(option => option.value) : [input.value]) : [];
        const active = selected.some(value => condition.values.includes(value));
        condition.field.hidden = !active;
        for (const {input, disabled} of condition.controls) {
          input.disabled = !active || disabled;
          input.required = active && condition.required && condition.field.dataset.fieldKind !== 'checkboxes';
          if (!active) input.setCustomValidity('');
        }
      }
    }
    syncConditionalFields();
    listen(form, 'input', syncConditionalFields);
    listen(form, 'change', syncConditionalFields);
    listen(form, 'reset', () => queueMicrotask(syncConditionalFields));
    if (document.readyState !== 'complete') listen(document, 'DOMContentLoaded', () => { restoreSubmittedState(); syncConditionalFields(); }, {once:true});
    if (receipt) {
      complete = root.dataset.previewSuccess === 'true' || hash === form.id || hash === root.id || roots.length === 1;
      receipt.hidden = !complete;
      body.hidden = complete;
      if (complete) {
        root.dataset.enquiryComplete = 'true';
        leadSuccess.publish();
        root.setAttribute('aria-labelledby', receipt.querySelector('h2').id);
      }
    }
    function position(element) {
      element.focus({preventScroll:true});
      (complete ? receipt : body.querySelector('.me-enquiry')).scrollIntoView({block:'start',behavior:'instant'});
    }
    function showStage(index, focus = false) {
      if (!stages.length || index < 0 || index >= stages.length) return;
      current = index;
      stages.forEach((stage, i) => stage.hidden = i !== index);
      if (submitAction) submitAction.hidden = index !== stages.length - 1;
      if (intro) {
        intro.textContent = stages[index].dataset.stageIntro || '';
        intro.hidden = !intro.textContent.trim();
      }
      progressItems.forEach((item, i) => {
        const button = item.querySelector('button');
        item.toggleAttribute('aria-current', false);
        if (i === index) item.setAttribute('aria-current', 'step');
        button.disabled = i >= index;
        if (i < index) button.setAttribute('aria-label', `${root.dataset.backLabel} ${stages[i].dataset.stageLabel}`);
        else button.removeAttribute('aria-label');
      });
      if (focus) position(stages[index].querySelector('h3') || root.querySelector('.me-heading h2'));
      scheduleSticky();
    }
    function checkGroups(container) {
      container.querySelectorAll('[data-field-kind="checkboxes"]').forEach(field => {
        const options = [...field.querySelectorAll('input')];
        if (options.length) options[0].setCustomValidity(field.dataset.fieldRequired === 'true' && !options.some(option => option.checked) ? root.dataset.choiceError : '');
      });
    }
    function validate(container) {
      syncConditionalFields();
      checkGroups(container);
      container.querySelectorAll('input,textarea').forEach(input => {
        if (!input.required || !input.matches('textarea,input:not([type]),input[type="text"],input[type="search"],input[type="email"],input[type="tel"],input[type="url"]')) return;
        if (input.value !== '' && input.value.trim() === '') {
          input.setCustomValidity(whitespaceError);
          whitespaceInvalid.add(input);
        } else if (whitespaceInvalid.delete(input)) input.setCustomValidity('');
      });
      const invalid = [...container.querySelectorAll('input,select,textarea')].find(input => !input.checkValidity());
      if (!invalid) return true;
      const stage = invalid.closest('[data-enquiry-stage]');
      if (stage) showStage(stages.indexOf(stage));
      position(stage?.querySelector('h3') || invalid);
      invalid.reportValidity();
      return false;
    }
    const keys = new Set(['enquiry_source']);
    const transportKeys = new Set(['enquiry_source']);
    let badConfiguration = !stages.length;
    fields.forEach(field => {
      const key = field.dataset.fieldKey;
      if (field.dataset.enquiryConditionKey && !fields.some(source => source.dataset.fieldKey === field.dataset.enquiryConditionKey)) badConfiguration = true;
      if (keys.has(key) || (field.dataset.fieldKind !== 'email' && key === 'email')) badConfiguration = true;
      if (field.dataset.fieldRole === 'custom' && ['name','phone','body','email'].includes(key)) badConfiguration = true;
      keys.add(key);
      const fieldTransportKeys = new Set();
      field.querySelectorAll('input,select,textarea').forEach(input => {
        const match = input.name.match(/^contact\[([^\]]+)\](\[\])?$/);
        if (!match) return;
        const transportKey = match[1];
        if (transportKeys.has(transportKey)) badConfiguration = true;
        if (fieldTransportKeys.has(transportKey) && field.dataset.fieldKind !== 'radio' && !(field.dataset.fieldKind === 'checkboxes' && match[2])) badConfiguration = true;
        fieldTransportKeys.add(transportKey);
      });
      fieldTransportKeys.forEach(key => transportKeys.add(key));
      if (['select','checkboxes','radio'].includes(field.dataset.fieldKind) && !field.querySelector('option:not([value=""]),input')) badConfiguration = true;
      const helper = field.querySelector('.me-field-help');
      if (helper) field.querySelectorAll('input,textarea,select').forEach(input => input.setAttribute('aria-describedby', helper.id));
    });
    if (badConfiguration) {
      const warning = body.querySelector('[data-enquiry-config-error]');
      warning.textContent = root.dataset.configError;
      warning.hidden = false;
      body.querySelectorAll('.me-submit button,.me-continue button').forEach(button => button.disabled = true);
      if (root.dataset.editor === 'true') console.warn('MAGMA enquiry: submission keys must be unique and choice fields need options.', root.id);
    }
    root.classList.add('me-enhanced');
    form.noValidate = true;
    if (progress) progress.hidden = stages.length < 2;
    body.querySelectorAll('[data-enquiry-continue]').forEach(action => {
      const stage = action.closest('[data-enquiry-stage]');
      action.hidden = stage === stages.at(-1) || stages.length < 2;
      listen(action.querySelector('button'), 'click', () => { if (validate(stages[current])) showStage(current + 1, true); });
    });
    progressItems.forEach((item, i) => listen(item.querySelector('button'), 'click', () => { if (i < current) showStage(i, true); }));
    listen(form, 'input', event => { if (whitespaceInvalid.delete(event.target)) event.target.setCustomValidity(''); });
    listen(form, 'change', event => { if (event.target.matches('input[type="checkbox"]')) checkGroups(form); });
    listen(form, 'keydown', event => {
      if (event.key === 'Enter' && !event.isComposing && event.target.matches('input:not([type="checkbox"]):not([type="radio"])') && current < stages.length - 1) {
        event.preventDefault();
        if (validate(stages[current])) showStage(current + 1, true);
      }
    });
    function resetSubmission() {
      clearTimeout(submitTimer);
      clearTimeout(submitFeedbackTimer);
      submitTimer = 0;
      submitFeedbackTimer = 0;
      pendingSubmitEvent = null;
      pendingCaptcha = false;
      submitting = false;
      if (submitButton) {
        submitButton.disabled = badConfiguration || submitInitiallyDisabled;
        submitButton.textContent = submitLabel;
      }
      if (initialBusy === null) form.removeAttribute('aria-busy');
      else form.setAttribute('aria-busy', initialBusy);
    }
    function preventSubmission(event) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
    listen(form, 'submit', event => {
      if (badConfiguration || complete || submitting || (pendingSubmitEvent && (!pendingSubmitEvent.defaultPrevented || pendingCaptcha))) { preventSubmission(event); return; }
      if (current < stages.length - 1) {
        preventSubmission(event);
        if (validate(stages[current])) showStage(current + 1, true);
        return;
      }
      if (!validate(body)) { preventSubmission(event); return; }
      leadSuccess.prepare();
      pendingSubmitEvent = event;
      pendingCaptcha = Boolean(form.dataset.hcaptchaBound);
      clearTimeout(submitFeedbackTimer);
      submitFeedbackTimer = setTimeout(() => {
        submitFeedbackTimer = 0;
        if (pendingSubmitEvent !== event) return;
        if (event.defaultPrevented && !pendingCaptcha) { pendingSubmitEvent = null; return; }
        submitting = true;
        if (submitButton) {
          submitButton.disabled = true;
          submitButton.textContent = root.dataset.submittingLabel;
        }
        form.setAttribute('aria-busy','true');
        if (!pendingCaptcha) submitTimer = setTimeout(resetSubmission, 20000);
      }, 0);
    }, {capture:true});
    listen(window, 'pageshow', event => { if (event.persisted) resetSubmission(); });
    function updateSticky() {
      frame = 0;
      if (!sticky) return;
      const viewport = window.visualViewport;
      const height = viewport ? viewport.height : innerHeight;
      const bounds = root.getBoundingClientRect();
      const inView = bounds.top < height - 20 && bounds.bottom > 0;
      if (inView) navigating = false;
      const typing = document.activeElement.matches('input,textarea,select');
      const keyboard = viewport && innerHeight - viewport.height > 120 && viewport.scale <= 1.05;
      const firstLink = [...document.querySelectorAll('a[href]')].find(link => !root.contains(link) && link.hash === `#${root.id}` && new URL(link.href).pathname === location.pathname);
      const waitingForFirstCTA = firstLink ? firstLink.getBoundingClientRect().bottom > 0 : scrollY < 180;
      const navigationOpen = Boolean(document.querySelector('button[aria-expanded="true"][aria-controls],.menu-toggle[aria-expanded="true"],dialog[open]'));
      const anotherSticky = [...instances.values()].some(instance => instance.root !== root && instance.sticky && !instance.sticky.hidden);
      sticky.hidden = !mobile.matches || complete || inView || typing || keyboard || waitingForFirstCTA || navigationOpen || anotherSticky || navigating;
      updateBody();
    }
    function scheduleSticky() { if (!frame) frame = requestAnimationFrame(updateSticky); }
    listen(window,'scroll',scheduleSticky,{passive:true});
    listen(window,'resize',scheduleSticky);
    listen(document,'focusin',scheduleSticky);
    listen(document,'focusout',scheduleSticky);
    listen(document,'click',scheduleSticky);
    if (window.visualViewport) {
      listen(window.visualViewport,'resize',scheduleSticky);
      listen(window.visualViewport,'scroll',scheduleSticky);
    }
    listen(document,'click',event => {
      const link = event.target.closest('a[href]');
      if (!link || link.hash !== `#${root.id}` || new URL(link.href).pathname !== location.pathname || event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      navigating = true;
      clearTimeout(navigationTimer);
      navigationTimer = setTimeout(() => { navigating = false; scheduleSticky(); },1500);
      if (sticky) sticky.hidden = true;
      updateBody();
      const heading = complete ? receipt.querySelector('h2') : root.querySelector('.me-heading h2');
      document.activeElement.blur();
      heading.focus({preventScroll:true});
      root.scrollIntoView({block:'start',behavior:reducedMotion.matches?'instant':'smooth'});
      history.replaceState(null,'',`#${root.id}`);
    });
    listen(document,'shopify:block:select',event => {
      if (!root.contains(event.target) || complete) return;
      const selected = event.target.closest('[data-enquiry-stage]');
      if (selected) showStage(stages.indexOf(selected));
      if (event.target.closest('[data-enquiry-success]')) return;
    });
    observer = new ResizeObserver(scheduleSticky);
    observer.observe(root);
    const instance = {root,sticky,destroy() {
      abort.abort();
      observer.disconnect();
      processObserver?.disconnect();
      cancelAnimationFrame(frame);
      clearTimeout(submitTimer);
      clearTimeout(submitFeedbackTimer);
      clearTimeout(navigationTimer);
      if (sticky) sticky.hidden = true;
      instances.delete(root);
      updateBody();
    }};
    instances.set(root,instance);
    const errorKeys = new Set((errors?.dataset.enquiryErrorKeys || '').split(',').map(key => key === 'author' ? 'name' : key));
    const errorStage = stages.findIndex(stage => [...stage.querySelectorAll('[data-enquiry-field]')].some(field =>
      errorKeys.has(field.dataset.fieldKey) || [...field.querySelectorAll('[name]')].some(input => errorKeys.has(input.name.match(/^contact\[([^\]]+)\]$/)?.[1]))
    ));
    showStage(errors ? (errorStage >= 0 ? errorStage : stages.length-1) : 0);
    if (complete) requestAnimationFrame(() => position(receipt.querySelector('h2')));
    else if (errors) requestAnimationFrame(() => position(errors));
    else if (hash === root.id || hash === form.id) {
      const restore = () => requestAnimationFrame(() => { root.scrollIntoView({block:'start',behavior:'instant'}); scheduleSticky(); });
      if (document.readyState === 'complete') restore();
      else listen(window,'load',restore,{once:true});
    }
    scheduleSticky();
  }
  function init(scope) {
    if (scope.matches?.('[data-magma-enquiry]')) mount(scope);
    scope.querySelectorAll('[data-magma-enquiry]').forEach(mount);
  }
  document.addEventListener('shopify:section:load',event => init(event.target));
  document.addEventListener('shopify:section:unload',event => {
    instances.forEach((instance,root) => { if (event.target.contains(root)) instance.destroy(); });
  });
  window.MagmaEnquiry = {init};
  init(document);
})();
